import { motion } from 'framer-motion'
import { LCARSBar } from '@enterpress/holomatrix'

/**
 * LCARS Frame - Decorative Star Trek-style border elements
 * Uses holomatrix LCARSBar components for consistency
 * 
 * Responsive:
 * - Mobile: Minimal corner accents only
 * - Tablet+: Full corner elements with vertical bars
 * - Desktop: Full frame with bottom bars
 */
export function LCARSFrame() {
  return (
    <>
      {/* Top-left corner - simplified on mobile */}
      <div className="fixed top-0 left-0 z-50 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          {/* Mobile: Small horizontal bar only */}
          <div className="absolute top-2 left-2 md:hidden">
            <LCARSBar color="amber" size="md" className="w-[60px]" />
          </div>
          
          {/* Tablet+: Full corner */}
          <div className="hidden md:block">
            {/* Horizontal bar */}
            <div className="absolute top-4 left-[70px]">
              <LCARSBar color="amber" size="lg" className="w-[180px]" />
            </div>
            {/* Vertical bar */}
            <div className="absolute top-[70px] left-4">
              <LCARSBar color="amber" size="lg" direction="vertical" className="h-[120px]" />
            </div>
            {/* Corner elbow - custom SVG for the rounded corner */}
            <svg width="70" height="70" viewBox="0 0 70 70" className="absolute top-4 left-4">
              <path
                d="M0 0 L50 0 L50 24 L24 24 L24 50 L0 50 Z"
                fill="#FFB347"
              />
              <path
                d="M24 24 Q24 36 36 36 L50 36 L50 24 L36 24 Q30 24 24 24"
                fill="#050508"
              />
            </svg>
            {/* Accent bars - positioned below the corner elbow */}
            <div className="absolute top-[56px] left-[70px] flex gap-1">
              <LCARSBar color="purple" size="sm" className="w-8" />
              <LCARSBar color="cyan" size="sm" className="w-12" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Top-right corner - simplified on mobile */}
      <div className="fixed top-0 right-0 z-50 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative"
        >
          {/* Mobile: Small horizontal bar + status */}
          <div className="md:hidden">
            <div className="absolute top-2 right-2">
              <LCARSBar color="cyan" size="md" className="w-[60px]" />
            </div>
            {/* Mobile status indicator */}
            <div className="absolute top-[22px] right-2 flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" />
              <span className="text-[8px] font-bold tracking-wider text-[#00D4FF] uppercase">
                Online
              </span>
            </div>
          </div>
          
          {/* Tablet+: Full corner */}
          <div className="hidden md:block">
            {/* Horizontal bar */}
            <div className="absolute top-4 right-[70px]">
              <LCARSBar color="cyan" size="lg" className="w-[180px]" />
            </div>
            {/* Vertical bar */}
            <div className="absolute top-[70px] right-4">
              <LCARSBar color="cyan" size="lg" direction="vertical" className="h-[120px]" />
            </div>
            {/* Corner elbow - mirrored */}
            <svg width="70" height="70" viewBox="0 0 70 70" className="absolute top-4 right-4">
              <path
                d="M70 0 L20 0 L20 24 L46 24 L46 50 L70 50 Z"
                fill="#00D4FF"
              />
              <path
                d="M46 24 Q46 36 34 36 L20 36 L20 24 L34 24 Q40 24 46 24"
                fill="#050508"
              />
            </svg>
            {/* Status indicator - positioned below the corner elbow */}
            <div className="absolute top-[56px] right-[70px] flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse shadow-[0_0_10px_#00FF88]" />
              <span className="text-[10px] font-bold tracking-wider text-[#00D4FF] uppercase">
                Online
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom decorative bars - visible on larger screens only */}
      <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none hidden lg:block">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="flex items-end justify-between px-6 pb-4"
        >
          {/* Left bottom accent */}
          <div className="flex items-end gap-2">
            <LCARSBar color="purple" size="sm" className="w-[80px]" />
            <LCARSBar color="amber" size="sm" className="w-[50px]" />
            <LCARSBar color="cyan" size="sm" className="w-[30px]" />
          </div>
          
          {/* Center stardate */}
          <div className="font-mono text-[10px] text-[rgba(255,179,71,0.6)] tracking-wider">
            USS ENTERPRESS • NCC-2026
          </div>
          
          {/* Right bottom accent */}
          <div className="flex items-end gap-2">
            <LCARSBar color="cyan" size="sm" className="w-[30px]" />
            <LCARSBar color="amber" size="sm" className="w-[50px]" />
            <LCARSBar color="purple" size="sm" className="w-[80px]" />
          </div>
        </motion.div>
      </div>
    </>
  )
}
